/**
 * Meta pixel events, fired twice on purpose.
 *
 * Every event goes to the browser pixel *and* to our API, both carrying the
 * same eventId. Meta collapses the pair, so a visitor whose browser blocks
 * connect.facebook.net (Safari ITP, Firefox strict, Brave, content blockers,
 * iOS Private Relay) still gets counted via the server copy — that blocking is
 * why Meta reports far fewer views than GA4 for the same traffic.
 */

const API_BASE = process.env.REACT_APP_API_URI ?? '/api';

/** Server mirroring is opt-in: see docs/META-CAPI.md for the Events Manager
 *  change that has to land first, otherwise PageView double-counts. */
const SERVER_MIRROR_ENABLED = process.env.REACT_APP_META_CAPI_ENABLED === 'true';

const readCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * crypto.randomUUID needs a secure context, which rules it out on plain-http
 * local dev. The fallback only has to be unique enough to pair two calls made
 * milliseconds apart.
 */
const newEventId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const sendToServer = (body) => {
  const url = `${API_BASE}/analytics/meta`;
  const json = JSON.stringify(body);

  // sendBeacon survives the page being torn down mid-navigation, which is
  // exactly when a PageView would otherwise be lost.
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const queued = navigator.sendBeacon(url, new Blob([json], { type: 'application/json' }));
    if (queued) return;
  }

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: json,
    keepalive: true,
  }).catch(() => {
    // Analytics is never worth surfacing an error for.
  });
};

/**
 * Fires `eventName` to both destinations.
 *
 * @param {string} eventName One of the events allowlisted by the API.
 * @param {object} [params]  Custom parameters for the browser pixel only —
 *                           the server copy stays minimal by design.
 */
export const trackMetaEvent = (eventName, params = {}) => {
  const eventId = newEventId();

  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('track', eventName, params, { eventID: eventId });
  }

  if (!SERVER_MIRROR_ENABLED) return eventId;

  sendToServer({
    eventName,
    eventId,
    eventSourceUrl: typeof window !== 'undefined' ? window.location.href : null,
    fbp: readCookie('_fbp'),
    fbc: readCookie('_fbc'),
  });

  return eventId;
};

export const trackMetaPageView = () => trackMetaEvent('PageView');

const metaConversionsService = { trackMetaEvent, trackMetaPageView };

export default metaConversionsService;
